/*───────────────────────────────────────────────────────────────────────────*\
  File      : src/utils/aiAggregatorApi.ts
  Purpose   : Client-side helper wrapping each /api/ai-aggregator/* endpoint
              with Promise functions.  Matches the style of userDataApi /
              userAuthApi: JSON routes use a shared post() helper while the
              analyze-image route transparently builds FormData for callers.
\*───────────────────────────────────────────────────────────────────────────*/

import type { AnalyzeRoomPropertiesWireResponse, GenerateRoomCanvasResponse } from "@/src/types/ai-api"

/*===========================================================================
  ▸ Internal helpers                                                        
===========================================================================*/

/**
 * POST JSON to route with credentials included.
 */
async function postJson<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })

  // Check HTTP status
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`)
  }

  const data = await res.json()

  if (data.success === false && data.error) {
    return { success: false, error: data.error } as T
  }

  return data as T
}

/**
 * POST FormData (no JSON header) to route with credentials.
 */
async function postForm<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    body: formData,
  })

  // Check HTTP status
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`)
  }

  const data = await res.json()

  if (data.success === false && data.error) {
    return { success: false, error: data.error } as T
  }

  return data as T
}

/*===========================================================================
  ▸ API wrappers                                                            
===========================================================================*/

/**
 * Analyze an uploaded room image to extract room type, dimensions, etc.
 *
 * @param image      Image file selected by user.
 * @param userId     UUID of the authenticated user (required).
 * @param sessionId  Previous session id for regeneration (optional).
 */
export function analyzeRoomProperties(
  image: File,
  userId: string, // CHANGED
  sessionId?: string,
): Promise<AnalyzeRoomPropertiesWireResponse> {
  if (!userId) throw new Error("userId is required") // ADDED
  const fd = new FormData()
  fd.append("image", image, image.name)
  fd.append("userid", userId) // ADDED
  if (sessionId) fd.append("sessionid", sessionId)
  return postForm("/api/ai-aggregator/analyze-room-properties", fd)
}

/**
 * Generate a blank canvas from an analyzed room.
 *
 * Wraps the streaming `/api/ai-aggregator/generate-room-canvas` route so
 * callers never touch fetch directly.
 *
 * Supports **either** JSON body with `imageUrl` **or** multipart **file upload**
 * with `imageFile` (optionally alongside `imageUrl`).
 *
 * @param payload.userId         UUID of the authenticated user (required).
 * @param payload.imageUrl       Optional URL of the source image (when not uploading a file).
 * @param payload.imageFile      Optional image File to upload (when not using imageUrl).
 * @param payload.roomType       Room type string.
 * @param payload.fixedElements  Array of fixed element labels.
 * @param payload.sessionId      Optional prior session id for continuity.
 * @param payload.landscape      Optional flag; when true, request landscape output (1536x1024).
 */
export function generateRoomCanvas(payload: {
  userId: string // ADDED
  imageUrl?: string
  imageFile?: File
  roomType: string
  fixedElements: string[]
  sessionId?: string
  landscape?: boolean
}): Promise<GenerateRoomCanvasResponse> {
  if (!payload.userId) throw new Error("userId is required") // ADDED
  const hasFile = !!payload.imageFile
  const hasUrl = typeof payload.imageUrl === "string" && payload.imageUrl.length > 0
  if (!hasFile && !hasUrl) {
    throw new Error("Either imageFile or imageUrl is required")
  }
  if (hasFile) {
    const fd = new FormData()
    fd.append("userid", payload.userId)
    fd.append("roomType", payload.roomType)
    for (const fe of payload.fixedElements || []) {
      fd.append("fixedElements", fe)
    }
    if (payload.sessionId) fd.append("sessionid", payload.sessionId)
    if (payload.landscape === true) fd.append("landscape", "true")
    if (hasUrl) fd.append("imageUrl", payload.imageUrl as string)
    fd.append("image", payload.imageFile as File, (payload.imageFile as File).name)

    console.log(payload)

    return postForm<GenerateRoomCanvasResponse>("/api/ai-aggregator/generate-room-canvas", fd)
  }

  const apiRequestData: any = {
    userid: payload.userId, // ADDED
    imageUrl: payload.imageUrl as string,
    roomType: payload.roomType,
    fixedElements: payload.fixedElements,
    sessionid: payload.sessionId ?? null,
  }
  if (payload.landscape === true) {
    apiRequestData.landscape = true
  }

  console.log(payload)

  return postJson<GenerateRoomCanvasResponse>("/api/ai-aggregator/generate-room-canvas", apiRequestData)
}

/**
 * Generate design concepts from scratch (Start-From-Scratch wizard).
 *
 * The API route returns `{ success, designs, sessionid }` which we type as:
 * @param payload.regeneration Optional boolean; when true, this call is a regeneration and should bill the *_REGEN action.
 */
export interface GenerateConceptsRouteResponse {
  success: boolean
  designs?: string[]
  sessionid?: string
  error?: string
}

export function generateRoomConcepts(payload: {
  userId: string // ADDED
  roomType: string
  roomTheme: string
  roomDimension: string
  colorDetails: string
  sessionId?: string
  regeneration?: boolean
}): Promise<GenerateConceptsRouteResponse> {
  if (!payload.userId) throw new Error("userId is required") // ADDED
  return postJson("/api/ai-aggregator/generate-room-concepts", {
    userid: payload.userId, // ADDED
    roomType: payload.roomType,
    roomTheme: payload.roomTheme,
    roomDimension: payload.roomDimension,
    colorDetails: payload.colorDetails,
    sessionid: payload.sessionId ?? null,
    regeneration: payload.regeneration ?? false,
  })
}

/**
 * Generate design concepts based on an existing canvas image.
 *
 * @param payload.regeneration Optional boolean; when true, this call is a regeneration and should bill the *_REGEN action.
 * @param payload.landscape    Optional flag; when true, request landscape output (1536x1024).
 */
export function generateRoomConceptsFromCanvas(payload: {
  userId: string // ADDED
  imageUrl: string
  roomType: string
  roomTheme: string
  colorDetails: string
  fixedElements: string[]
  sessionId?: string
  regeneration?: boolean
  landscape?: boolean
}): Promise<GenerateConceptsRouteResponse> {
  if (!payload.userId) throw new Error("userId is required") // ADDED
  const apiRequestData: any = {
    userid: payload.userId, // ADDED
    imageUrl: payload.imageUrl,
    roomType: payload.roomType,
    roomTheme: payload.roomTheme,
    colorDetails: payload.colorDetails,
    fixedElements: payload.fixedElements,
    sessionid: payload.sessionId ?? null,
    regeneration: payload.regeneration ?? false,
  }
  if (payload.landscape === true) {
    apiRequestData.landscape = true
  }
  return postJson("/api/ai-aggregator/generate-room-concepts-from-canvas", apiRequestData)
}

/*──────────────────────────── End of file ────────────────────────────────*/
